import EpdSelectComponent from 'components/form/EpdSelectComponent';
import HelpIcon from 'components/icons/HelpIcon';
import arrowDown from 'images/arrow-down.png';
import arrowUp from 'images/arrow-up.png';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionState, EPDModel } from 'services/EpdClient';
import styled from 'styled-components';
import { InfoBox, Label } from 'styles/Styles.styled';
import { Option } from 'types/types';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import { FixedLabelTop, FixedRow } from '../../../pages/wizard/styles';
import { Validation } from '../../../pages/wizard/Validation';
import { MaterialPropertiesInfo } from './MaterialPropertiesInfo';
import {
  AddButton,
  ArrowDownBox,
  ArrowImage,
  ArrowUpBox,
  Box,
  MaterialColumn1,
  MaterialColumn2,
  MaterialColumn3,
  MaterialGrid,
  RemoveButton,
} from '../styles';

import { useMaterialPropertiesLookup } from 'services/api/queries';
import { ErrorAlertPanel } from 'components/v2/compiler/panels/Styles.styled';
import { ActionMeta } from 'react-select';

interface Props {
  epd: EPDModel | undefined;
  validation: any;
  infoArrowItems: Record<string, boolean>;
  toggleOpen: (field: string) => void;
  onChangeEpd: (field: string, value: any) => void;
  SelectStyle: any;
}

export const MaterialPropertiesSection: React.FC<Props> = ({
  epd,
  validation,
  infoArrowItems,
  toggleOpen,
  onChangeEpd,
  SelectStyle,
}) => {
  const { t } = useTranslation();
  const isDisabled = epd?.materialPropertiesState?.actionState === ActionState.Disabled;
  const [epdMaterialProperties, setEpdMaterialProperties] = React.useState<any[]>([]);

  const materialPropertiesLookup = useMaterialPropertiesLookup().data;

  useEffect(() => {
    if (!materialPropertiesLookup) return;
    const epdMaterialProperties = epd?.materialProperties?.map((item: any) => {
      const res2 = materialPropertiesLookup.find((x) => x.id === item.materialPropertyId);
      return {
        id: res2?.id,
        name: res2?.fullName,
        value: item.value,
      };
    });
    setEpdMaterialProperties(epdMaterialProperties ?? []);
  }, [materialPropertiesLookup]);

  const createRow = () => {
    const newRowValues = [...epdMaterialProperties, { id: '', name: '', value: '' }];
    setEpdMaterialProperties(newRowValues);
  };

  const saveRowValues = (values: any[]) => {
    const filteredRows = values.filter((x) => x.id !== '' && x.value !== '');
    const valuesToSave: any = filteredRows.map((row) => {
      return {
        MaterialPropertyId: row.id,
        Value: row.value,
      };
    });

    onChangeEpd('MaterialProperties', valuesToSave);
  };

  const deleteRow = (index: number) => {
    const newValues = [...epdMaterialProperties];
    newValues.splice(index, 1);
    setEpdMaterialProperties([...newValues]);
    saveRowValues(newValues);
  };

  const onChangeMaterialUnit = (data: any, index: number, actionMeta: ActionMeta<Option>) => {
    const newValues = [...epdMaterialProperties];
    newValues[index] = { value: newValues[index].value, id: data?.value, name: data?.label };
    setEpdMaterialProperties(newValues);
    if (actionMeta.action === 'clear') return;
    saveRowValues(newValues.filter((mp) => !!mp.id && !!mp.value));
  };

  const onChangeMaterialValue = (value: number, index: number, save: boolean = false) => {
    const newValues = [...epdMaterialProperties];
    newValues[index].value = value;
    setEpdMaterialProperties(newValues);
    if (save) {
      saveRowValues(newValues.filter((mp) => !!mp.id && !!mp.value));
    }
  };

  // Check if the EPD is new based on the createdAt date - this is weird definition
  const isEpdNew = !epd?.createdAt || new Date(epd.createdAt) > new Date('2025-07-22T00:00:00Z');

  return (
    <FixedRow style={{ marginBottom: '1rem' }} state={epd?.materialPropertiesState?.actionState}>
      <FixedLabelTop>
        {t('epdWizard.wizardStep2.properties.materialProperties.title')}
        <HelpIcon content={t('epdWizard.wizardStep2.tooltips.materialProperties')} />
        <Validation validation={validation} epd={epd} field="materialProperties" />
      </FixedLabelTop>

      <Box style={{ paddingTop: '0.5rem' }}>
        <div style={{ width: '100%', paddingBottom: '1rem' }}>
          <InfoBox style={{ alignItems: 'center', padding: '0.5rem 0', marginBottom: 0, borderLeft: 'none' }}>
            {infoArrowItems.materialProperties ? (
              <ArrowUpBox>
                <MaterialPropertiesInfo />
              </ArrowUpBox>
            ) : (
              <ArrowDownBox>{t('epdWizard.wizardStep2.info.materialProperties.declaration')}</ArrowDownBox>
            )}

            {t('epdWizard.wizardStep2.info.materialProperties.declaration').length >= 73 &&
              (infoArrowItems.materialProperties ? (
                <ArrowImage src={arrowUp} alt="Arrow up" onClick={() => toggleOpen('materialProperties')} />
              ) : (
                <ArrowImage src={arrowDown} alt="Arrow down" onClick={() => toggleOpen('materialProperties')} />
              ))}
          </InfoBox>
          {epd?.materialPropertiesState?.errorMessage &&
            !epd?.materialPropertiesState?.errorMessage.startsWith('Material properties are required for construction') && (
              <ErrorAlertPanel className="w-full">{epd?.materialPropertiesState?.errorMessage}</ErrorAlertPanel>
            )}
        </div>

        <MaterialGrid>
          <MaterialColumn1>
            <Label>{t('epdWizard.wizardStep2.properties.materialProperties.propertyUnit')}</Label>
          </MaterialColumn1>
          <MaterialColumn2>
            <Label>{t('epdWizard.wizardStep2.properties.materialProperties.value')}</Label>
          </MaterialColumn2>
          <div></div>

          {epdMaterialProperties.map((item, index) => (
            <React.Fragment key={index}>
              <MaterialColumn1>
                <EpdSelectComponent
                  options={(materialPropertiesLookup || [])
                    .filter((x) => {
                      if (epdMaterialProperties.some((row) => row.id === x.id)) return false;
                      if (x.fullName?.startsWith('Conversion factor to mass')) return false;

                      if (
                        isEpdNew &&
                        (x.fullName?.startsWith('Grammage (kg') || x.fullName?.startsWith('Gross density (kg'))
                      ) {
                        return false;
                      }

                      return true;
                    })
                    .map(
                      (x): Option => ({
                        value: x.id || '',
                        label: x.fullName || '',
                      })
                    )}
                  value={{ value: item?.id, label: item?.name }}
                  isMulti={false}
                  isDisabled={isDisabled}
                  styles={SelectStyle}
                  isClearable
                  onChange={(e: Option, actionMeta: ActionMeta<Option>) => onChangeMaterialUnit(e, index, actionMeta)}
                  {...reactSelectPropsForHtmlLabel}
                />
              </MaterialColumn1>

              <MaterialColumn2>
                <MaterialInput
                  value={item.value}
                  type="number"
                  placeholder={t('placeholder.insertNumber')}
                  disabled={isDisabled}
                  onChange={(e) => onChangeMaterialValue(e.target.value as any, index, false)}
                  onBlur={(e) => onChangeMaterialValue(e.target.value as any, index, true)}
                />
              </MaterialColumn2>

              <MaterialColumn3 style={{ textAlign: 'center' }}>
                <RemoveButton type="button" onClick={() => deleteRow(index)} disabled={isDisabled}>
                  {t('remove')}
                </RemoveButton>
              </MaterialColumn3>
            </React.Fragment>
          ))}

          <MaterialColumn3>
            {epdMaterialProperties.length < 6 && (
              <AddButton
                type="button"
                onClick={createRow}
                disabled={isDisabled || epdMaterialProperties[epdMaterialProperties.length - 1] === null}
              >
                + {t('add')}
              </AddButton>
            )}
          </MaterialColumn3>
        </MaterialGrid>
      </Box>
    </FixedRow>
  );
};

const MaterialInput = styled.input`
  border-radius: 0;
  border-color: gray;
  border-width: 1px;
  padding-top: 0;
  padding-left: 8px;
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  white-space: nowrap;
  overflow: hidden;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

export default MaterialPropertiesSection;
